/**
 * Domain Reports Tab Component
 *
 * Reusable component for displaying reports filtered by domain.
 * Used within management pages to show domain-specific reports.
 */

import { useEffect, useState, type ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  Briefcase,
  Clock,
  Database,
  Download,
  FileText,
  Server,
  Settings2,
  Users,
} from 'lucide-react'
import { reportsApi } from '../../services'
import type { ReportMetadata, ReportDomain } from '../../types'
import { logger } from '../../utils/logger'
import {
  ReportsEmpty,
  ReportsError,
  ReportsFooter,
  ReportsHeader,
  ReportsLoading,
} from './domain-reports-tab/domain-reports-tab.parts'

// Map icon names to Lucide components
const iconMap: Record<string, React.ElementType> = {
  Clock: Clock,
  Users: Users,
  Briefcase: Briefcase,
  Database: Database,
  Server: Server,
  FileText: FileText,
}

// Map domains to colours
const domainColourMap: Record<ReportDomain, { bg: string; text: string }> = {
  licensing: {
    bg: 'bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10',
    text: 'text-[#5c6e94] dark:text-[#8a9bba]',
  },
  users: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
  },
  cases: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
  },
  'data-usage': {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-700 dark:text-purple-300',
  },
  infrastructure: {
    bg: 'bg-stone-50 dark:bg-stone-800/50',
    text: 'text-stone-700 dark:text-stone-300',
  },
}

interface ReportListItemProps {
  report: ReportMetadata
}

function ReportListItem({ report }: ReportListItemProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const Icon = iconMap[report.icon] ?? FileText
  const colours = domainColourMap[report.domain]

  return (
    <Link
      to={report.path}
      className="group flex items-center gap-4 rounded-lg border border-stone-200 bg-white p-4 transition-all hover:border-[#8a9bba] hover:shadow-md dark:border-stone-800 dark:bg-stone-900 dark:hover:border-[#5c6e94]"
    >
      <div className={`rounded-lg p-3 ${colours.bg}`}>
        <Icon className={`h-6 w-6 ${colours.text}`} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-stone-900 dark:text-stone-100">{report.name}</h3>
        <p className="mt-1 truncate text-sm text-stone-600 dark:text-stone-400">
          {report.description}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex gap-2">
          {report.queryable && (
            <span
              className="rounded-md bg-stone-100 p-1.5 dark:bg-stone-800"
              title={t('reports.supportsFilters')}
            >
              <Settings2 className="h-4 w-4 text-stone-500 dark:text-stone-400" />
            </span>
          )}
          {report.exportable && (
            <span
              className="rounded-md bg-stone-100 p-1.5 dark:bg-stone-800"
              title={t('reports.exportable')}
            >
              <Download className="h-4 w-4 text-stone-500 dark:text-stone-400" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm font-medium text-[#5c6e94] transition-colors group-hover:text-[#4e5e82] dark:text-[#8a9bba]">
          {t('reports.view')}{' '}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  )
}

interface DomainReportsTabProps {
  domain: ReportDomain
}

export function DomainReportsTab({ domain }: DomainReportsTabProps): ReactElement {
  const { t } = useTranslation('admin')
  const [reports, setReports] = useState<ReportMetadata[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchReports = async (): Promise<void> => {
      try {
        setLoading(true)
        const data = await reportsApi.getReportsByDomain(domain)
        setReports(data)
      } catch (err) {
        logger.error('DomainReportsTab', 'Failed to fetch reports', err)
        setError(t('reports.loadError'))
      } finally {
        setLoading(false)
      }
    }

    void fetchReports()
  }, [domain, t])

  if (loading) return <ReportsLoading />
  if (error) return <ReportsError message={error} retryLabel={t('reports.retry')} />
  if (reports.length === 0) return <ReportsEmpty domain={domain} t={t} />

  return (
    <div className="space-y-6">
      <ReportsHeader domain={domain} t={t} />
      <div className="space-y-3">
        {reports.map((report) => (
          <ReportListItem key={report.id} report={report} />
        ))}
      </div>
      <ReportsFooter t={t} />
    </div>
  )
}
